import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { API_HOST } from '../../utils/api';
import AssetPreviewModal from '../shared/AssetPreviewModal';
import { HiOutlineUsers, HiOutlineDocumentDuplicate, HiOutlineTag, HiOutlineFolder, HiOutlineUserGroup, HiOutlineEye, HiOutlineDownload, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi';

const ASSET_LABELS = {
  blog: 'Blog', newsletter: 'Newsletter', email_sequence: 'Email Seq.',
  one_pager: 'One-Pager', banner: 'Banner', service_page: 'Svc Page', presentation: 'Presentation',
  marketing_strategy: 'Marketing Strategy',
  cs_asset_1: 'CS Asset 1', cs_asset_2: 'CS Asset 2', cs_asset_3: 'CS Asset 3', cs_asset_4: 'CS Asset 4',
  sales_proposal: 'Proposal', sales_deck: 'Sales Deck',
  customer_overview: 'Overview', customer_status: 'Status Report', customer_one_pager: 'One-Pager (Sales)',
};

const DASHBOARD_BADGE = {
  blog: 'Partner', newsletter: 'Partner', email_sequence: 'Partner', one_pager: 'Partner',
  banner: 'Partner', service_page: 'Partner', presentation: 'Partner', marketing_strategy: 'Partner',
  cs_asset_1: 'CS', cs_asset_2: 'CS', cs_asset_3: 'CS', cs_asset_4: 'CS',
  sales_proposal: 'Sales', sales_deck: 'Sales',
  customer_overview: 'Sales', customer_status: 'Sales', customer_one_pager: 'Sales',
};

const BADGE_COLORS = {
  Partner: 'bg-blue-100 text-blue-700',
  Sales: 'bg-amber-100 text-amber-700',
  CS: 'bg-teal-100 text-teal-700',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [showAssets, setShowAssets] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetFilter, setAssetFilter] = useState('all');
  const [previewAsset, setPreviewAsset] = useState(null);

  useEffect(() => {
    api.get('/admin/stats').then(({ data }) => {
      setStats(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadAssets = async () => {
    if (assets.length > 0) { setShowAssets(!showAssets); return; }
    setAssetsLoading(true);
    try {
      const { data } = await api.get('/admin/assets');
      setAssets(data);
      setShowAssets(true);
    } catch { }
    setAssetsLoading(false);
  };

  const filteredAssets = assetFilter === 'all'
    ? assets
    : assets.filter(a => DASHBOARD_BADGE[a.assetType] === assetFilter);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/admin/partners" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineUsers className="w-8 h-8 text-v2-blue mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.totalPartners || 0}</div>
          <div className="text-sm text-gray-500">Total Partners</div>
        </Link>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <HiOutlineDocumentDuplicate className="w-8 h-8 text-green-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.assetsThisMonth || 0}</div>
          <div className="text-sm text-gray-500">Assets This Month</div>
        </div>
        <Link to="/admin/topics" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineTag className="w-8 h-8 text-purple-600 mb-2" />
          <div className="text-sm text-gray-500 mt-2">Manage Topics</div>
        </Link>
        <Link to="/admin/library" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineFolder className="w-8 h-8 text-yellow-600 mb-2" />
          <div className="text-sm text-gray-500 mt-2">Manage Library</div>
        </Link>
        <Link to="/admin/users" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineUsers className="w-8 h-8 text-teal-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.totalMarketingUsers || 0}</div>
          <div className="text-sm text-gray-500">Marketing Users</div>
        </Link>
        <Link to="/admin/users" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineUsers className="w-8 h-8 text-orange-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.totalSalesUsers || 0}</div>
          <div className="text-sm text-gray-500">Sales Users</div>
        </Link>
        <Link to="/admin/users" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
          <HiOutlineUsers className="w-8 h-8 text-amber-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.totalPMs || 0}</div>
          <div className="text-sm text-gray-500">Partner Managers</div>
        </Link>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <HiOutlineUserGroup className="w-8 h-8 text-indigo-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">{stats?.activePanels || 0}</div>
          <div className="text-sm text-gray-500">Active Panels</div>
        </div>
      </div>

      {/* Assets by type */}
      {stats?.assetsByType && stats.assetsByType.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Assets by Type (This Month)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.assetsByType.map(item => (
              <div key={item.assetType} className="text-center">
                <div className="text-2xl font-bold text-gray-900">{item._count}</div>
                <div className="text-sm text-gray-500">{ASSET_LABELS[item.assetType] || item.assetType}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Assets Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button onClick={loadAssets}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 text-left">
          <div className="flex items-center gap-3">
            <HiOutlineDocumentDuplicate className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-800">All Generated Assets</h2>
            {assets.length > 0 && <span className="text-sm text-gray-400">({assets.length})</span>}
          </div>
          {assetsLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-v2-blue" />
          ) : showAssets ? (
            <HiOutlineChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <HiOutlineChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showAssets && (
          <div className="border-t border-gray-200">
            {/* Filters */}
            <div className="px-6 py-3 bg-gray-50 flex items-center gap-2">
              <span className="text-sm text-gray-500">Filter:</span>
              {['all', 'Partner', 'Sales', 'CS'].map(f => (
                <button key={f} onClick={() => setAssetFilter(f)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    assetFilter === f ? 'bg-v2-blue text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}>
                  {f === 'all' ? 'All' : f} {f !== 'all' && `(${assets.filter(a => DASHBOARD_BADGE[a.assetType] === f).length})`}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Topic</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created By</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAssets.slice(0, 50).map(asset => {
                    const dashboard = DASHBOARD_BADGE[asset.assetType];
                    const canPreview = asset.status === 'completed' && asset.filePath && ['html', 'pdf', 'docx'].includes(asset.fileFormat);
                    return (
                      <tr key={asset.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{ASSET_LABELS[asset.assetType] || asset.assetType}</span>
                            {dashboard && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${BADGE_COLORS[dashboard]}`}>{dashboard}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{asset.topic?.title || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {asset.partner?.companyName || asset.dashboardProfile?.displayName || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{new Date(asset.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            asset.status === 'completed' ? 'bg-green-100 text-green-700' :
                            asset.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>{asset.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-center">
                            {canPreview && (
                              <button onClick={() => setPreviewAsset(asset)} className="text-gray-400 hover:text-v2-blue" title="Preview">
                                <HiOutlineEye className="w-4 h-4" />
                              </button>
                            )}
                            {asset.status === 'completed' && asset.filePath && (
                              <a href={`${API_HOST}/api/admin/assets/${asset.id}/preview`} target="_blank" rel="noopener noreferrer"
                                className="text-gray-400 hover:text-v2-blue" title="Open">
                                <HiOutlineDownload className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredAssets.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">No assets found</div>
              )}
              {filteredAssets.length > 50 && (
                <div className="text-center py-3 text-xs text-gray-400">Showing first 50 of {filteredAssets.length} assets</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewAsset && (
        <AssetPreviewModal
          previewUrl={`${API_HOST}/api/admin/assets/${previewAsset.id}/preview`}
          downloadUrl={`${API_HOST}/api/admin/assets/${previewAsset.id}/preview`}
          fileFormat={previewAsset.fileFormat}
          assetLabel={ASSET_LABELS[previewAsset.assetType] || previewAsset.assetType}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </div>
  );
}
